/* eslint-disable no-extra-boolean-cast */
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { toClose } from './LeftSidebarActions';
import styles from './LeftSidebarMenuItem.scss';
import { getMergedClassNames } from '../../utils/HTMLUtils';
import { DownIcon } from 'components/Icons';

const rootClassName = styles.rootClassName;

function toURL(input) {
  try {
    return new URL(input);
  } catch {
    return new URL(input, window.location.origin);
  }
}

function normalizeBase(base) {
  if (!base) return null;
  const u = toURL(base);
  let href = u.href.toLowerCase();
  if (!href.endsWith('/')) href += '/';
  return href;
}

function isExternal(candidate, externalBases) {
  const url = toURL(candidate);
  const sameOrigin = url.origin === window.location.origin;
  if (!sameOrigin) return true;
  const normalized = url.href.toLowerCase();
  return externalBases.some((b) => normalized.startsWith(b));
}

const LeftSidebarMenuItem = ({ id, label, path, normalIcon, hoverIcon, subMenus }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useDispatch();
  const isActive = window.location.pathname === path;

  const externalBases = useMemo(() => {
    const bases = [
      window.env.auth?.PE_WEB_URL?.gestao_cadastro,
      window.env.auth?.PE_WEB_URL?.registro_pedagogico,
      window.env.auth?.PE_WEB_URL?.home,
    ]
      .map(normalizeBase)
      .filter(Boolean);
    return bases;
  }, []);

  const isExternalLink = useMemo(() => {
    return isExternal(path, externalBases);
  }, [path, externalBases]);

  const toggleSubMenu = useCallback(() => {
    setIsOpen((prevIsOpen) => !prevIsOpen);
  }, []);

  const handleExternalClick = useCallback(
    (targetPath) => {
      dispatch(toClose());
      window.location.assign(targetPath);
    },
    [dispatch]
  );

  const handleClick = () => {
    if (isExternalLink) {
      handleExternalClick(path);
    } else {
      dispatch(toClose());
    }
  };

  const DownIconButton = React.memo(() => (
    <div className={`${rootClassName}-dropdown-icon ${isOpen ? 'opened' : ''}`}>
      <DownIcon />
    </div>
  ));

  const Content = () => (
    <>
      <div className={rootClassName + '-icon-container'}>
        {typeof normalIcon === 'string' ? (
          <img
            src={normalIcon}
            className={getMergedClassNames([
              rootClassName + '-icon',
              rootClassName + '-normal-icon',
            ])}
            alt="icone-normal"
            loading="lazy"
          />
        ) : (
          <div
            className={getMergedClassNames([
              rootClassName + '-material-icon',
              rootClassName + '-material-normal-icon',
            ])}
          >
            {normalIcon}
          </div>
        )}
        {typeof hoverIcon === 'string' ? (
          <img
            src={hoverIcon}
            className={getMergedClassNames([
              rootClassName + '-icon',
              rootClassName + '-hover-icon',
            ])}
            alt="icone-hover"
            loading="lazy"
          />
        ) : (
          <div
            className={getMergedClassNames([
              rootClassName + '-material-icon',
              rootClassName + '-material-hover-icon',
            ])}
          >
            {hoverIcon}
          </div>
        )}
      </div>
      <span className={`${rootClassName}-label ${isActive ? 'actived' : ''}`}>
        {label}
      </span>
    </>
  );

  return (
    <div className={rootClassName}>
      {!!subMenus.length ? (
        <button
          className={`${rootClassName}-navlink ${isOpen ? 'opened' : ''}`}
          onClick={toggleSubMenu}
        >
          <Content />
          <DownIconButton />
        </button>
      ) : isExternalLink ? (
        <button id={id} className={`${rootClassName}-navlink`} onClick={handleClick}>
          <Content />
        </button>
      ) : (
        <NavLink
          id={id}
          className={`${rootClassName}-navlink`}
          to={path}
          activeClassName="actived"
          onClick={() => dispatch(toClose())}
        >
          <Content />
        </NavLink>
      )}

      {isOpen && !!subMenus.length && (
        <ul className={`${rootClassName}-submenu`}>
          {subMenus.map(({ idSubMenu, pathSubMenu, labelSubMenu }) => {
            const subIsExternal = isExternal(pathSubMenu, externalBases);
            return subIsExternal ? (
              <button
                id={idSubMenu}
                key={idSubMenu}
                className={`${rootClassName}-navlink`}
                onClick={() => handleExternalClick(pathSubMenu)}
              >
                {labelSubMenu}
              </button>
            ) : (
              <NavLink
                id={idSubMenu}
                key={idSubMenu}
                className={`${rootClassName}-navlink`}
                to={pathSubMenu}
                activeClassName="actived"
                onClick={() => dispatch(toClose())}
              >
                {labelSubMenu}
              </NavLink>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default LeftSidebarMenuItem;